import { useState, useEffect } from "react"
import { motion, AnimatePresence, PanInfo } from "framer-motion"
import {
  ChevronLeft, ChevronRight, Reply, Mail, User, Calendar, 
  ChevronDown, ChevronUp, X, ArrowLeft, ArrowRight, LoaderIcon, UserCircle
} from "lucide-react"
import { cn } from "@/lib/utils"
import { EmailData } from "@/types"
import { UnifiedEmailComposer } from "./UnifiedEmailComposer"

interface EmailCarouselProps {
  emails: EmailData[]
  onReply?: (email: EmailData) => Promise<string | void>
  onSendEmail?: (email: { to: string; subject: string; content: string }, originalEmail?: EmailData) => Promise<void>
  onAIModifyEmail?: (instruction: string, currentEmail: { to: string; subject: string; content: string }) => Promise<{ to: string; subject: string; content: string }>
  onClose?: () => void
  className?: string
  respondedIds?: Set<string>
}

export function EmailCarousel({
  emails,
  onReply,
  onSendEmail,
  onAIModifyEmail,
  onClose,
  className,
  respondedIds
}: EmailCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set())
  const [isGeneratingReply, setIsGeneratingReply] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [activeReplyComposer, setActiveReplyComposer] = useState<{
    to: string
    subject: string
    content: string
    emailId: string
  } | null>(null)

  const currentEmail = emails[currentIndex]
  const isResponded = respondedIds?.has(currentEmail?.id) || currentEmail?.isResponded

  // Helper functions
  const formatSender = (from: string) => {
    // Extract name from "Name <email@example.com>" format
    const match = from.match(/^(.*?)\s*<.*>$/);
    return match ? match[1].trim() : from;
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'long' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  // Keyboard navigation - DISABLED
  // useEffect(() => {
  //   const handleKeyDown = (event: KeyboardEvent) => {
  //     if (event.key === 'ArrowLeft') {
  //       event.preventDefault()
  //       navigateEmail('prev')
  //     } else if (event.key === 'ArrowRight') {
  //       event.preventDefault()
  //       navigateEmail('next')
  //     } else if (event.key === 'r' && !event.ctrlKey && !event.metaKey && !isGeneratingReply) {
  //       // Press 'r' to reply to current email
  //       event.preventDefault()
  //       handleReply(currentEmail)
  //     }
  //   }

  //   window.addEventListener('keydown', handleKeyDown)
  //   return () => window.removeEventListener('keydown', handleKeyDown)
  // }, [currentIndex, emails, isGeneratingReply])

  const navigateEmail = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentIndex(prev => prev > 0 ? prev - 1 : emails.length - 1)
    } else {
      setCurrentIndex(prev => prev < emails.length - 1 ? prev + 1 : 0)
    }
  }

  const goToEmail = (index: number) => {
    setCurrentIndex(index)
  }

  const handleReply = async (email: EmailData) => {
    if (isGeneratingReply) return
    if (respondedIds?.has(email.id)) {
      setNotice('Already responded to this thread');
      setTimeout(() => setNotice(null), 2500);
      return
    }
    
    setIsGeneratingReply(true)
    // Open inline composer immediately with placeholders
    const initialSubject = email.subject?.startsWith('Re: ') ? email.subject : `Re: ${email.subject || ''}`
    setActiveReplyComposer({
      to: email.from,
      subject: initialSubject,
      content: 'Generating AI reply...',
      emailId: email.id
    })
    try {
      const generated = onReply ? await onReply(email) : undefined
      if (generated && typeof generated === 'string') {
        setActiveReplyComposer(prev => prev ? { ...prev, content: generated } : prev)
      } else {
        // If no generated content, leave placeholder for manual typing
        setActiveReplyComposer(prev => prev ? { ...prev, content: '' } : prev)
      }
    } catch (error) {
      console.error('Error generating reply:', error)
      setActiveReplyComposer(prev => prev ? { ...prev, content: '[Error generating AI reply. Please write your reply manually.]' } : prev)
    } finally {
      setIsGeneratingReply(false)
    }
  }

  const toggleExpanded = (emailId: string) => {
    setExpandedEmails(prev => {
      const next = new Set(prev)
      if (next.has(emailId)) {
        next.delete(emailId)
      } else {
        next.add(emailId)
      }
      return next
    })
  }

  const handleDragEnd = (event: any, info: PanInfo) => {
    const threshold = 50
    if (info.offset.x > threshold) {
      navigateEmail('prev')
    } else if (info.offset.x < -threshold) {
      navigateEmail('next')
    }
  }

  if (!emails.length) {
    return (
      <div className="text-center text-white/60 py-8">
        No emails to display
      </div>
    )
  }

  const fullBody = currentEmail.fullBody || currentEmail.snippet || ""
  const previewLength = 200
  const needsExpansion = fullBody.length > previewLength
  const isExpanded = expandedEmails.has(currentEmail.id)
  const displayBody = needsExpansion && !isExpanded 
    ? fullBody.substring(0, previewLength) + "..."
    : fullBody

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-w-4xl mx-auto",
        className
      )}
    >
      {/* Header with navigation */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-blue-400" />
          <div>
            <h2 className="text-white/90 font-medium text-base">Email Carousel</h2>
            <p className="text-white/60 text-sm">
              {currentIndex + 1} of {emails.length} emails
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Navigation buttons */}
          <button
            onClick={() => navigateEmail('prev')}
            disabled={emails.length <= 1}
            className="p-2 text-white/60 hover:text-white/90 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-lg">
            <span className="text-sm text-white/80 font-medium">
              {currentIndex + 1}
            </span>
            <span className="text-sm text-white/50">/</span>
            <span className="text-sm text-white/60">
              {emails.length}
            </span>
          </div>

          <button
            onClick={() => navigateEmail('next')}
            disabled={emails.length <= 1}
            className="p-2 text-white/60 hover:text-white/90 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white/80 rounded-lg transition-colors ml-2"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Email dots indicator */}
      {emails.length > 1 && (
        <div className="flex justify-center gap-1 p-3 border-b border-white/5">
          {emails.map((_, index) => (
            <button
              key={index}
              onClick={() => goToEmail(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                index === currentIndex
                  ? "bg-blue-400 w-6"
                  : "bg-white/20 hover:bg-white/40"
              )}
            />
          ))}
        </div>
      )}

      {/* Email content area */}
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentEmail.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            className="p-6 cursor-grab active:cursor-grabbing"
          >
            {notice && (
              <div className="mb-3 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs px-3 py-2">
                {notice}
              </div>
            )}
            {/* Email header */}
            <div className="px-4 py-3 border-b border-slate-700/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white line-clamp-2 break-words mb-2">
                    {currentEmail.subject || "(No subject)"}
                  </h3>
                  <div className="flex items-center gap-2 text-xs">
                    <User className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-300 truncate">{formatSender(currentEmail.from)}</span>
                    {currentEmail.accountEmail && (
                      <>
                        <span className="text-slate-500">•</span>
                        <UserCircle className="w-3 h-3 text-blue-400" />
                        <span className="text-blue-400 truncate">{currentEmail.accountEmail}</span>
                      </>
                    )}
                    <span className="text-slate-500">•</span>
                    <Calendar className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-400">{formatDate(currentEmail.date)}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleReply(currentEmail)}
                                     disabled={isGeneratingReply || isResponded}
                  className={cn(
                    "px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 font-medium flex-shrink-0",
                    isGeneratingReply || isResponded
                      ? "bg-blue-500/10 text-blue-500/50 cursor-not-allowed"
                      : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
                  )}
                >
                  {isGeneratingReply ? (
                    <>
                      <LoaderIcon className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Reply className="w-4 h-4" />
                      {isResponded ? 'Responded' : 'Reply'}
                    </>
                  )}
                </button>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-3 text-white/70">
                  <User className="w-4 h-4 text-white/50" />
                  <span className="text-white/60 min-w-[3rem]">From:</span>
                  <span className="text-white/90">{currentEmail.from}</span>
                </div>
                
                {currentEmail.to && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Mail className="w-4 h-4 text-white/50" />
                    <span className="text-white/60 min-w-[3rem]">To:</span>
                    <span className="text-white/90">{currentEmail.to}</span>
                  </div>
                )}
                
                {currentEmail.date && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Calendar className="w-4 h-4 text-white/50" />
                    <span className="text-white/60 min-w-[3rem]">Date:</span>
                    <span className="text-white/90">{currentEmail.date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Email body */}
            <div className="border-t border-white/10 pt-6">
              <div className="text-white/80 whitespace-pre-wrap text-sm leading-relaxed">
                {displayBody}
              </div>
              
              {needsExpansion && (
                <button
                  onClick={() => toggleExpanded(currentEmail.id)}
                  className="mt-4 flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show full email
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Reply composer */}
            <AnimatePresence>
              {activeReplyComposer && activeReplyComposer.emailId === currentEmail.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-6 border-t border-white/10 pt-6"
                >
                  <UnifiedEmailComposer
                    initialTo={activeReplyComposer.to}
                    initialSubject={activeReplyComposer.subject}
                    initialContent={activeReplyComposer.content}
                    onSend={async (email) => {
                      if (onSendEmail) {
                        await onSendEmail(email, currentEmail)
                      }
                      // Close composer and move to next email
                      setActiveReplyComposer(null)
                      // Advance to next email
                      setCurrentIndex(prev => (prev < emails.length - 1 ? prev + 1 : 0))
                    }}
                    onAIModify={onAIModifyEmail}
                    onClose={() => setActiveReplyComposer(null)}
                    className="bg-white/5 border-white/20"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer with quick actions */}
      <div className="p-4 border-t border-white/10 bg-white/5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-white/60">
            <div className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              <span>Use buttons to navigate</span>
            </div>
            <div className="flex items-center gap-2">
              <Reply className="w-4 h-4" />
              <span>Click Reply button</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {currentEmail.hasAttachments && (
              <div className="text-white/60">
                📎 {currentEmail.attachments?.length || 1} attachment(s)
              </div>
            )}
            
            <div className="text-white/60">
              {currentEmail.isRead ? '✓ Read' : '● Unread'}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
} 